(function (window) {
  'use strict';
  if (window.StackhouseUploadResilience) return;

  var MB = 1024 * 1024;
  var DEFAULT_MULTIPART_THRESHOLD = 100 * MB;
  var DEFAULT_PART_SIZE = 64 * MB;
  var DEFAULT_TIMEOUT = 5 * 60 * 1000;

  function emit(eventName, details) {
    var payload = details || {};
    var tracking = window.stackhouseBuilderAnalytics || window.stackhouseTracking;

    if (tracking && typeof tracking.emit === 'function' && (window.stackhouseBuilderAnalytics || typeof tracking.flush === 'function')) {
      tracking.emit(eventName, payload);
      return;
    }

    window.pendingStackhouseEmitQueue = Array.isArray(window.pendingStackhouseEmitQueue)
      ? window.pendingStackhouseEmitQueue
      : [];
    window.pendingStackhouseEmitQueue.push({ eventName: eventName, details: payload });
  }

  function connectionConcurrency() {
    var connection = window.navigator && window.navigator.connection;
    var effectiveType = connection && connection.effectiveType;
    var saveData = connection && connection.saveData;
    var mobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var cores = window.navigator && window.navigator.hardwareConcurrency || 4;

    if (saveData || mobile || effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
      return Math.min(2, cores);
    }

    return Math.min(4, Math.max(2, cores));
  }

  function createPlan(file, options) {
    options = options || {};
    var threshold = typeof options.multipartThresholdBytes === 'number'
      ? options.multipartThresholdBytes
      : DEFAULT_MULTIPART_THRESHOLD;
    var partSize = options.partSizeBytes || DEFAULT_PART_SIZE;
    var multipart = Boolean(file && file.size > threshold);
    var totalParts = multipart ? Math.ceil(file.size / partSize) : 1;

    return {
      multipart: multipart,
      partSizeBytes: multipart ? partSize : file.size,
      totalParts: totalParts,
      concurrency: multipart ? Math.min(totalParts, options.concurrency || connectionConcurrency()) : 1
    };
  }

  function wait(milliseconds) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, milliseconds);
    });
  }

  var transferSequence = 0;

  function transferId() {
    transferSequence += 1;
    return 'transfer-' + Date.now().toString(36) + '-' + transferSequence.toString(36)
      + '-' + Math.random().toString(36).slice(2, 12);
  }

  // One bounded outcome per part attempt. Never include filenames, storage URLs,
  // response bodies, or ETags in this diagnostic stream.
  function reportAttempt(state, error, partIndex, attempt, instanceId, transfer) {
    if (state.reported) return;
    state.reported = true;
    try {
      var telemetry = window.StackhouseOperationalTelemetry;
      if (!telemetry || typeof telemetry.emit !== 'function') return;
      var percent = state.size ? Math.floor(state.loaded / state.size * 100) : 0;
      var band = !state.loaded ? 'none' : percent < 25 ? 'under_25' : percent < 50 ? '25_to_49'
        : percent < 75 ? '50_to_74' : percent < 100 ? '75_to_99' : '100';
      var age = state.lastProgressAt === null ? null : Math.max(0, Date.now() - state.lastProgressAt);
      var ageBand = age === null ? 'never' : age < 5000 ? 'under_5s' : age < 30000 ? '5_to_30s'
        : age < 120000 ? '30_to_120s' : 'over_120s';
      var navigator = window.navigator || {};
      var effectiveType = navigator.connection && navigator.connection.effectiveType;
      telemetry.emit({
        workflow: 'upload_request_trace',
        workflow_instance_id: instanceId,
        attempt_id: transfer + '-part-' + (partIndex + 1) + '-try-' + (attempt + 1),
        component: 'upload_transfer',
        stage: 'asset_transfer',
        operation: 'asset_transfer',
        event: error ? 'upload_request_failed' : 'upload_request_completed',
        status: error ? 'failed' : 'succeeded',
        severity: error ? 'warning' : 'info',
        duration_ms: Math.max(0, Date.now() - state.startedAt),
        retry_count: attempt,
        status_code: state.status || 0,
        error_code: error ? (error.code || 'UPLOAD_PART_FAILED') : '',
        safe_message: error ? 'Storage transfer attempt failed.' : 'Storage transfer attempt completed.',
        safe_context: {
          operation: 'asset_transfer',
          request_kind: 'upload_part',
          step: state.responseReceived ? 'response_received' : state.bodyComplete ? 'awaiting_response'
            : state.loaded ? 'sending_body' : 'no_progress',
          diagnostic_result: 'progress_' + band + '.last_progress_' + ageBand,
          retry_count: attempt,
          online: typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined,
          visibility_state: window.document && /^(visible|hidden)$/.test(window.document.visibilityState)
            ? window.document.visibilityState : 'unknown',
          network_effective_type: /^(slow-2g|2g|3g|4g)$/.test(effectiveType) ? effectiveType : 'unknown'
        }
      });
    } catch (diagnosticError) {}
  }

  function uploadRequest(url, blob, options, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var settled = false;

      function finish(error, result) {
        if (settled) return;
        settled = true;
        if (error) reject(error);
        else resolve(result);
      }

      function recordProgress(loaded) {
        if (settled || !Number.isFinite(loaded)) return;
        loaded = Math.min(blob.size, Math.max(0, loaded));
        if (loaded <= options.state.loaded) return;
        options.state.loaded = loaded;
        options.state.lastProgressAt = Date.now();
        options.state.bodyComplete = loaded === blob.size;
        // Raw byte activity must survive a retry below the displayed high water mark.
        try {
          if (typeof options.onActivity === 'function') options.onActivity();
        } catch (activityError) {}
        onProgress(loaded);
      }

      xhr.upload.addEventListener('progress', function (event) {
        recordProgress(event.loaded);
      });
      xhr.upload.addEventListener('load', function () {
        recordProgress(blob.size);
      });
      xhr.addEventListener('load', function () {
        if (settled) return;
        options.state.responseReceived = true;
        options.state.status = xhr.status;
        if (xhr.status >= 200 && xhr.status < 300) {
          finish(null, (xhr.getResponseHeader('ETag') || '').replace(/"/g, ''));
        } else {
          var statusError = new Error('Upload storage returned status ' + xhr.status + '.');
          statusError.status = xhr.status;
          finish(statusError);
        }
      });
      xhr.addEventListener('error', function () {
        finish(new Error('The network connection was interrupted during upload.'));
      });
      xhr.addEventListener('timeout', function () {
        var timeoutError = new Error('The upload paused because the connection timed out.');
        timeoutError.code = 'UPLOAD_TIMEOUT';
        finish(timeoutError);
      });
      xhr.addEventListener('abort', function () {
        var abortError = new Error('The upload was canceled.');
        abortError.code = 'UPLOAD_ABORTED';
        finish(abortError);
      });
      xhr.open('PUT', url, true);
      xhr.timeout = options.partTimeoutMs || DEFAULT_TIMEOUT;
      if (options.setContentType && options.fileType) {
        xhr.setRequestHeader('Content-Type', options.fileType);
      }
      xhr.send(blob);
    });
  }

  async function upload(options) {
    var file = options.file;
    var plan = options.plan || createPlan(file);
    var urls = options.urls || [];
    var maxRetries = typeof options.maxRetries === 'number' ? options.maxRetries : 3;
    var progressByPart = new Array(plan.totalParts).fill(0);
    var completedBytes = 0;
    var retryCount = 0;
    var nextPartIndex = 0;
    var parts = [];
    var startedAt = Date.now();
    var lastPercent = 0;
    var transfer = transferId();
    var assetId = options.eventDetails && options.eventDetails.upload_asset_id;
    var instanceId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(assetId)
      ? assetId : transfer;
    var eventDetails = Object.assign({
      file_size_bytes: file.size,
      file_type: file.type || 'application/octet-stream',
      file_count: plan.totalParts
    }, options.eventDetails || {});

    if (urls.length < plan.totalParts) {
      throw new Error('Upload session did not provide enough storage URLs.');
    }

    function updateProgress(partIndex, loaded) {
      progressByPart[partIndex] = loaded;
      var inFlightBytes = progressByPart.reduce(function (total, value) { return total + value; }, 0);
      var percent = Math.min(100, Math.floor((completedBytes + inFlightBytes) / file.size * 100));
      if (percent > lastPercent && typeof options.onProgress === 'function') {
        lastPercent = percent;
        options.onProgress(percent);
      }
    }

    async function uploadPart(partIndex) {
      var start = partIndex * plan.partSizeBytes;
      var end = Math.min(start + plan.partSizeBytes, file.size);
      var blob = file.slice(start, end);
      var attempt = 0;

      while (attempt <= maxRetries) {
        progressByPart[partIndex] = 0;
        var state = { startedAt: Date.now(), size: blob.size, loaded: 0,
          lastProgressAt: null, bodyComplete: false, responseReceived: false, status: 0 };
        try {
          var etag = await uploadRequest(urls[partIndex], blob, {
            state: state,
            onActivity: options.onActivity,
            partTimeoutMs: options.partTimeoutMs,
            setContentType: options.setContentType && !plan.multipart,
            fileType: file.type
          }, function (loaded) {
            updateProgress(partIndex, loaded);
          });

          if (plan.multipart && !etag) {
            var etagError = new Error('Upload storage did not return a part confirmation.');
            etagError.code = 'MISSING_ETAG';
            throw etagError;
          }

          reportAttempt(state, null, partIndex, attempt, instanceId, transfer);
          progressByPart[partIndex] = 0;
          completedBytes += blob.size;
          var completedPercent = Math.min(100, Math.floor(completedBytes / file.size * 100));
          if (completedPercent > lastPercent && typeof options.onProgress === 'function') {
            lastPercent = completedPercent;
            options.onProgress(completedPercent);
          }
          if (plan.multipart) parts.push({ PartNumber: partIndex + 1, ETag: etag });
          return;
        } catch (error) {
          reportAttempt(state, error, partIndex, attempt, instanceId, transfer);
          var retryableStatus = !error.status || error.status === 408 || error.status === 429 || error.status >= 500;
          if (attempt >= maxRetries || !retryableStatus) {
            error.code = error.code || 'UPLOAD_PART_FAILED';
            error.partNumber = partIndex + 1;
            error.retryCount = retryCount;
            throw error;
          }

          attempt += 1;
          retryCount += 1;
          emit('file_upload_part_retried', Object.assign({}, eventDetails, {
            failure_phase: 'transfer',
            retry_count: retryCount,
            status: 'retrying',
            error_code: error.code || 'NETWORK_ERROR'
          }));
          if (typeof options.onRetry === 'function') options.onRetry(partIndex + 1, attempt, error);
          await wait(Math.min(4000, 500 * Math.pow(2, attempt - 1)) + Math.floor(Math.random() * 250));
        }
      }
    }

    async function worker() {
      while (nextPartIndex < plan.totalParts) {
        var partIndex = nextPartIndex;
        nextPartIndex += 1;
        await uploadPart(partIndex);
      }
    }

    emit('file_upload_transfer_started', Object.assign({}, eventDetails, { status: 'started' }));

    try {
      await Promise.all(new Array(plan.concurrency).fill(null).map(worker));
      parts.sort(function (a, b) { return a.PartNumber - b.PartNumber; });
      emit('file_upload_transfer_completed', Object.assign({}, eventDetails, {
        duration_ms: Date.now() - startedAt,
        retry_count: retryCount,
        status: 'completed'
      }));
      return { parts: parts, retryCount: retryCount, durationMs: Date.now() - startedAt };
    } catch (error) {
      emit('file_upload_transfer_failed', Object.assign({}, eventDetails, {
        duration_ms: Date.now() - startedAt,
        failure_phase: 'transfer',
        retry_count: retryCount,
        status: 'failed',
        error_code: error.code || 'UPLOAD_PART_FAILED',
        status_code: error.status || 0
      }));
      throw error;
    }
  }

  async function confirmCrop(options) {
    options = options || {};
    var fetchRequest = options.fetch;
    var submitUrl = String(options.apiBaseUrl || '').replace(/\/$/, '') + '/api/check-crop/';
    var payload = options.payload || {};
    var eventDetails = options.eventDetails || {};
    var startedAt = Date.now();
    var pollIntervalMs = options.pollIntervalMs || 1500;
    var maxWaitMs = options.maxWaitMs || 5 * 60 * 1000;
    var requestUrl = submitUrl;
    var requestOptions = postOptions();
    var retryableFailures = 0;
    var pollCount = 0;
    var transportTimeoutCount = 0;
    var pendingReported = false;

    if (typeof fetchRequest !== 'function') {
      throw new Error('Crop confirmation transport is unavailable.');
    }

    function postOptions() {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      };
    }

    function reportProgress(message) {
      if (typeof options.onProgress === 'function') options.onProgress(message);
    }

    function report(eventName, details) {
      emit(eventName, Object.assign({}, eventDetails, {
        step: 'confirmation',
        poll_count: pollCount,
        transport_timeout_count: transportTimeoutCount,
        retry_count: retryableFailures + transportTimeoutCount,
        duration_ms: Date.now() - startedAt
      }, details || {}));
    }

    report('crop_confirmation_started');
    while (Date.now() - startedAt < maxWaitMs) {
      var response;
      try {
        response = await fetchRequest(requestUrl, requestOptions);
      } catch (error) {
        if (!error || error.name !== 'AbortError') {
          report('crop_confirmation_failed', {
            error_code: 'CROP_CONFIRMATION_NETWORK_FAILED',
            confirmation_status: 'network_failed',
            failure_phase: requestOptions.method === 'GET'
              ? 'confirmation_poll'
              : 'confirmation_submit'
          });
          throw error;
        }
        transportTimeoutCount += 1;
        report('crop_confirmation_network_retried', {
          error_code: 'CROP_CONFIRMATION_TRANSPORT_TIMEOUT',
          confirmation_status: requestOptions.method === 'GET' ? 'polling' : 'submitting'
        });
        reportProgress('Still verifying crop...');
        await wait(pollIntervalMs);
        continue;
      }

      var responseData = await response.json().catch(function () { return {}; });
      if (responseData.confirmation_id) {
        eventDetails = Object.assign({}, eventDetails, {
          attempt_id: String(responseData.confirmation_id)
        });
      }
      if (response.status === 202) {
        pollCount += 1;
        if (!pendingReported) {
          pendingReported = true;
          report('crop_confirmation_pending', {
            status_code: 202,
            confirmation_status: responseData.status || 'processing'
          });
        }
        reportProgress('Verifying crop...');
        requestUrl = responseData.status_url || requestUrl;
        requestOptions = { method: 'GET' };
        await wait(Math.max(500, Number(responseData.retry_after_ms) || pollIntervalMs));
        continue;
      }

      if (response.ok) {
        report('crop_confirmation_completed', {
          status_code: response.status,
          confirmation_status: responseData.status || 'success'
        });
        return responseData;
      }

      if (response.status === 503 && responseData.retryable && retryableFailures < 3) {
        retryableFailures += 1;
        report('crop_confirmation_retry_started', {
          status_code: response.status,
          error_code: responseData.error_type || 'CROP_CONFIRMATION_RETRYABLE',
          confirmation_status: responseData.status || 'failed'
        });
        reportProgress('Retrying crop verification...');
        requestUrl = submitUrl;
        requestOptions = postOptions();
        await wait(pollIntervalMs * retryableFailures);
        continue;
      }

      report('crop_confirmation_failed', {
        status_code: response.status,
        error_code: responseData.error_type || 'CROP_CONFIRMATION_FAILED',
        confirmation_status: responseData.status || 'failed'
      });
      if (typeof options.responseError === 'function') {
        throw options.responseError(
          responseData,
          responseData.error || 'Crop confirmation failed (' + response.status + ')'
        );
      }
      throw new Error(responseData.error || 'Crop confirmation failed (' + response.status + ')');
    }

    report('crop_confirmation_timed_out', {
      status_code: 408,
      error_code: 'CROP_CONFIRMATION_CLIENT_TIMEOUT',
      confirmation_status: 'timed_out'
    });
    throw new Error('Crop verification is taking longer than expected. Your crop is still saved, so please wait a moment and try continuing again.');
  }

  window.StackhouseUploadResilience = {
    createPlan: createPlan,
    confirmCrop: confirmCrop,
    upload: upload,
    emit: emit,
    constants: {
      maxFileSizeBytes: 1024 * MB,
      multipartThresholdBytes: DEFAULT_MULTIPART_THRESHOLD,
      partSizeBytes: DEFAULT_PART_SIZE
    }
  };
})(window);
